import {
  Box,
  Button,
  FormControl,
  List,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { fontSize, palette } from '../../../..';
import { flowOverrideTypes, TFlowOverrideType } from '../../constants';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDispatch, useSelector } from 'react-redux';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  updateFilterOverride,
  updateIsFilterGroup,
} from '../../../../redux/Calculations/CalculationsActions';
import { RootState } from '../../../../redux';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {
  IFlowOverrideField,
  IOverrideFilterPanel,
} from '../../../../redux/Calculations/CurrentCalculationSessionInterface';
import FilterOverrideDropZone from '../../DropZones/FilterOverrideDropZone';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OverrideItem from './OverrideItem';
import { CanvasSelect } from '../../common/CanvasSelect';
interface IFilterOverrideProps {
  propKey: string;
  filterOverrideInfo: {
    type: TFlowOverrideType;
    filterGroups: string[];
    isFilterGroup: boolean;
    filters: IFlowOverrideField[];
    filterPanel: IOverrideFilterPanel;
  };
  sx?: React.CSSProperties;
  ensureNodeVisible: () => void
}

function FilterOverride({ sx, filterOverrideInfo, propKey, ensureNodeVisible }: IFilterOverrideProps) {
  const dispatch = useDispatch();
  const [hideFilterOverride, setHideFilterOverride] = useState(false);
  const { groups } = useSelector((state: RootState) => {
    const selectedDs:
      | {
        connectionId: string;
        id: string;
        isFlatFileData: boolean;
        datasetName: string;
        workSpaceId: string;
      }
      | undefined = state.chartProperties.properties[propKey]?.selectedDs;
    let groups = Object.keys(state.chartFilterGroup.groups || {}).reduce(
      (acc, grpId) => {
        const group = state.chartFilterGroup.groups?.[grpId];
        if (group?.dataSetId === selectedDs?.id) {
          acc.push({
            groupId: grpId,
            groupName: group.name,
          });
        }
        return acc;
      },
      [] as { groupId: string; groupName: string }[],
    );
    groups.unshift({ groupId: 'sys#chartfilter_root1', groupName: 'Chart Filter' });
    return {
      selectedDs,
      groups,
    };
  });
  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value as TFlowOverrideType;
    const newFilterOverrideInfo = {
      ...filterOverrideInfo,
      type: value,
      ...(value.includes('All')
        ? {
          filters: [],
          filterGroups: [],
          isFilterGroup: false,
          filterPanel: {
            panelName: 'overrideFilter' as const,
            shouldAllConditionsMatch: true,
            filters: [],
          },
        }
        : {
          filters: filterOverrideInfo.isFilterGroup
            ? []
            : filterOverrideInfo.filters.map((filter) => ({
              ...filter,
              customization: false,
            })),
          filterGroups: filterOverrideInfo.isFilterGroup ? filterOverrideInfo.filterGroups : [],
          isFilterGroup: filterOverrideInfo.isFilterGroup,
          filterPanel: {
            panelName: 'overrideFilter' as const,
            shouldAllConditionsMatch: true,
            filters: value === 'ignoreSome' ? [] : filterOverrideInfo.filterPanel.filters,
          },
        }),
    };
    //if value is honor some filterpanel for not available fields in filterpanel
    dispatch(updateFilterOverride(propKey, newFilterOverrideInfo));
    if (value.includes("Some")) ensureNodeVisible()
  };
  const handleSwitchChange = (isFilterGroup: boolean) => {
    dispatch(updateIsFilterGroup(propKey, isFilterGroup));
  };
  const updateFilterOverrides = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const groupId = event.target.name;
    let updatedValues = [...filterOverrideInfo.filterGroups];

    if (checked) {
      updatedValues.push(groupId);
    } else {
      updatedValues = updatedValues.filter((id) => id !== groupId);
    }

    dispatch(
      updateFilterOverride(propKey, {
        ...filterOverrideInfo,
        filterGroups: updatedValues,
        isFilterGroup: true,
      }),
    );
  };
  return (
    <Box
      sx={{
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: "2px" }}>
        <Typography
          sx={{
            color: '#484848',
            fontSize: fontSize.large,
            fontWeight: 'bold',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}
        >
          Filter Override
          <Tooltip
            placement="top"
            title={
              <Typography
                sx={{
                  color: 'white',
                }}
              >
                Honor All - Honors all filters from base filter. <br />
                Ignore All - Ignore all filters from base filter. <br />
                Honor Some - Honors selected filters from base filter. <br />
                Ignore Some - Ignores selected filters from base filter.
              </Typography>
            }
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <InfoOutlineIcon fontSize="small" />
          </Tooltip>
        </Typography>
        {!hideFilterOverride ? (
          <KeyboardArrowDownIcon
            onClick={() => setHideFilterOverride(true)}
            sx={{ cursor: 'pointer', color: palette.primary.contrastText, mr: 0.6 }}
          />
        ) : (
          <KeyboardArrowRightIcon
            onClick={() => setHideFilterOverride(false)}
              sx={{ cursor: 'pointer', color: palette.primary.contrastText, mr: 0.6 }}
          />
        )}
      </Box>
      {!hideFilterOverride && (
        <>
          <Box sx={{ mt: 1, mb: filterOverrideInfo.type.includes('All') ? 2 : 0 }}>
            <CanvasSelect<(typeof flowOverrideTypes)[number]>
              value={flowOverrideTypes.find((t) => t.key === filterOverrideInfo.type) ?? null}
              options={flowOverrideTypes}
              getLabel={(opt) => opt.value}
              getKey={(opt) => opt.key}
              onChange={(val) =>
                handleChange({ target: { value: val.key } } as any)
              }
              style={{ height: "2rem" }}
            />
          </Box>
          {!filterOverrideInfo.type.includes('All') && (
            <div style={{ display: 'flex', marginTop: '0.5rem' }}>
              <Tooltip
                title="Columns"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <Button
                  size="small"
                  onClick={() => handleSwitchChange(false)}
                  sx={{
                    backgroundColor: !filterOverrideInfo.isFilterGroup
                      ? 'rgb(224, 224, 224)'
                      : 'white',
                    color: '#484848',
                    borderRadius: '5px',
                    border: '1px solid lightgray',
                    borderRight: 'none',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',

                    '&:hover': {
                      backgroundColor: !filterOverrideInfo.isFilterGroup
                        ? 'rgb(224, 224, 224)'
                        : 'white',
                    },
                  }}
                >
                  Columns
                </Button>
              </Tooltip>

              <Tooltip
                title="Filter Groups"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <Button
                  size="small"
                  onClick={() => handleSwitchChange(true)}
                  sx={{
                    backgroundColor: filterOverrideInfo.isFilterGroup
                      ? 'rgb(224, 224, 224)'
                      : 'white',
                    color: '#484848',
                    borderRadius: '5px',
                    border: '1px solid lightgray',
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    // cursor: selectedLabel === button2labelTxt ? 'default' : 'pointer',
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: filterOverrideInfo.isFilterGroup
                        ? 'rgb(224, 224, 224)'
                        : 'white',
                    },
                  }}
                >
                  Filter Groups
                </Button>
              </Tooltip>
            </div>
          )}
          {!filterOverrideInfo.type.includes('All') && filterOverrideInfo.isFilterGroup && (
            <Box sx={{ marginTop: '0.5rem' }}>
              <Typography
                sx={{
                  color: '#484848',
                  fontSize: fontSize.large,
                  fontWeight: 'bold',
                  textAlign: 'left',
                  mb: 1,
                }}
              >
                Filter Groups
              </Typography>
              <FormGroup
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  alignItems: 'flex-start',
                  mb: 1,
                }}
              >
                {groups.map((group, index) => (
                  <FormControlLabel
                    sx={{
                      marginLeft: 0,
                      marginRight: 0,
                    }}
                    control={
                      <Checkbox
                        name={group.groupId}
                        size="small"
                        onChange={updateFilterOverrides}
                        checked={filterOverrideInfo.filterGroups.includes(group.groupId)}
                        sx={{
                          padding: 0,
                          paddingRight: '0.5rem',
                          '&.Mui-checked': {
                            color: filterOverrideInfo.type.includes('ignore')
                              ? 'orange'
                              : '#2bb9bb',
                          },
                        }}
                      />
                    }
                    label={
                      <span
                        title={group.groupName}
                        style={{
                          color: palette.primary.contrastText,
                          textDecoration:
                            filterOverrideInfo.type.includes('ignore') &&
                              filterOverrideInfo.filterGroups.includes(group.groupId)
                              ? 'line-through'
                              : 'none',
                        }}
                      >
                        {group.groupName}
                      </span>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          )}
          {!filterOverrideInfo.type.includes('All') && !filterOverrideInfo.isFilterGroup && (
            <FilterOverrideDropZone propKey={propKey}>
              {filterOverrideInfo.filters.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Typography
                    sx={{
                      color: '#484848',
                      fontSize: fontSize.extraSmall,
                      textAlign: 'center',
                      py: "1.5rem"
                    }}
                  >
                    Drag and drop fields here to override filters
                  </Typography>
                </Box>)
                :
                (<List
                  sx={{
                    pt: 0,
                  }}
                >
                  {
                    filterOverrideInfo.filters.map((filter, index) => {
                      const filterInfo = filterOverrideInfo.filterPanel.filters.find(
                        (_filter) => _filter.filterId === filter.uid,
                      );

                      return (
                        <OverrideItem
                          propKey={propKey}
                          filterId={filter.uid}
                          calculationUid={filter.calculatedFieldId ?? null}
                          filterInfo={filterInfo}
                          overrideType={filterOverrideInfo.type}
                          customized={filter.customization}
                          displayName={filter.displayName}
                          dataType={filter.dataType}
                          fieldName={filter.fieldName}
                          tableId={filter.tableId}
                          ensureNodeVisible={ensureNodeVisible}
                        />
                      );
                    })
                  }
                </List>)
              }
            </FilterOverrideDropZone>
          )}
        </>
      )}
    </Box>
  );
}

export default FilterOverride;
